import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'
import { useField, useFormikContext } from 'formik'
import { useNewTranslation } from 'hooks'
import { createTeam, TeamState } from 'state/teams'
import { MODAL_TYPE } from 'components/Team/common/Modals'
import { selectIsInternalUser, selectTeamList } from 'state/profile'
import Modal, { CreateTeamValuesState } from './index'
import styled from 'particles/themes/styled'
import Label from 'particles/Label'
import { useTranslation } from 'react-i18next'
import Text from 'particles/Text'
import { Theme } from 'particles/themes'
import { selectTeamConstants } from 'state/constants'
import { getTeamTypeOptions } from 'components/Team/Teams/utils'
import { selectTeamFilter } from 'state/filter'
import { FormikNumberInput } from 'particles/NumberInput'
import { usePermission } from 'hooks/permissions'
import { OtherExternalTeamManagement } from 'components/Shared/Permission/enums'
import Toggle, { FormikToggle } from 'particles/Toggle'
interface CreateEditTeamModalProps {
    onClose: () => void
    type: MODAL_TYPE
    team?: TeamState
    className?: string
}

export const ToggleAiCaptionReview = ({ isReadOnly }: { isReadOnly: boolean }) => {
    const [t] = useNewTranslation()
    const { values, setFieldValue } = useFormikContext<CreateTeamValuesState>()

    return (
        <Toggle
            isChecked={values.is_ai_caption_review_enabled}
            handleChange={() => {
                const updateValue = !Boolean(values.is_ai_caption_review_enabled)
                setFieldValue('is_ai_caption_review_enabled', updateValue)
            }}
            label={t('manageTeams.allTeams.enableAIReviewTitle')}
            subtext={t('manageTeams.allTeams.enableAIReviewSubtext')}
            isReadOnly={isReadOnly}
        />
    )
}

const CreateTeam: React.FC<CreateEditTeamModalProps> = ({ onClose, team, className }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const isInternalUser = useSelector(selectIsInternalUser)
    const teamConstants = useSelector(selectTeamConstants)
    const teamList = useSelector(selectTeamList)
    const teamFilter = useSelector(selectTeamFilter)
    const canManageRoles = usePermission(OtherExternalTeamManagement.ManageRoles) && !team?.is_internal
    const isAiCaptionReviewReadonly = isInternalUser && !canManageRoles
    const isAiCaptionReviewShow = isInternalUser && canManageRoles
    const currentTeam = useMemo(() => {
        return teamList.find(team => team.id === teamFilter)
    }, [teamFilter, teamList])
    const canSeePartipostFee = (() => {
        if (team?.is_internal) {
            return false
        }

        if (isInternalUser) {
            return true
        }

        return team?.type === 'saas'
    })()

    const validationSchema = yup.object({
        name: yup.string().nullable().required(t('errorMessage.validation.enterTeamName')),
        ...(canSeePartipostFee
            ? {
                  service_fee_percentage: yup
                      .number()
                      .nullable()
                      .test({
                          message: () => t('manageTeams.allTeams.defaultPartipostFeeOverRangeError'),
                          test: value => {
                              if (value > 100 || value < 0) {
                                  return false
                              }
                              return true
                          },
                      })
                      .required(t('manageTeams.allTeams.defaultPartipostFeeOverRangeError')),
              }
            : {}),
        type: yup.string().nullable().required(),
        ambassador_enabled: yup.boolean().nullable(),
    })

    const initialTeamType = (() => {
        if (isInternalUser) {
            return 'enterprise_managed_service'
        }

        return currentTeam?.type
    })()

    const initialValues = {
        name: team && team.name,
        service_fee_percentage: isInternalUser
            ? teamConstants?.default_service_fee_percentage
            : currentTeam?.service_fee_percentage ?? 0,
        is_ai_caption_review_enabled: false,
        type: initialTeamType,
        ambassador_enabled: team?.ambassador_enabled,
    }
    const onSubmit = (value: CreateTeamValuesState) => {
        dispatch(
            createTeam({
                name: value.name ?? '',
                type: value.type ?? '',
                service_fee_percentage: value.service_fee_percentage ?? 0,
                ambassador_enabled: value.ambassador_enabled ?? false,
                isInternalUser,
                is_ai_caption_review_enabled: value.is_ai_caption_review_enabled,
            }),
        )
        onClose()
    }

    return (
        <div className={className}>
            <Modal
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={(values: CreateTeamValuesState) => {
                    if (!values.name) return
                    onSubmit(values)
                }}
                onClose={onClose}
                title={t('modalWindow.createTeamWindowTitle')}
                submitButtonText={t('button.createTeam')}
                size="m"
            >
                <Modal.TextInput
                    name="name"
                    label={t('modalWindow.enterTeamNameHeader')}
                    subtext={t('modalWindow.enterTeamNameSubtext')}
                    placeholder={t('placeholderText.enterTeamName')}
                    isRequired
                    width="100%"
                />

                <TeamTypeSelectField showError isReadOnly={!isInternalUser} currentType={currentTeam?.type} />

                {canSeePartipostFee && (
                    <FormikNumberInput
                        name="service_fee_percentage"
                        label={t('manageTeams.allTeams.defaultPartipostFeeTitle')}
                        subtext={t('manageTeams.allTeams.defaultPartipostFeeSubtext')}
                        placeholder={'0'}
                        isRequired
                        width="100%"
                        isReadOnly={!canManageRoles}
                    />
                )}
                {isAiCaptionReviewShow && <ToggleAiCaptionReview isReadOnly={isAiCaptionReviewReadonly} />}
                {canManageRoles && (
                    <FormikToggle
                        name="ambassador_enabled"
                        label={t('manageTeams.allTeams.enableAmbassadorsTrackerTitle')}
                        subtext={t('manageTeams.allTeams.enableAmbassadorsTrackerSubText')}
                        isReadOnly={!canManageRoles}
                    />
                )}
            </Modal>
        </div>
    )
}

export default styled(CreateTeam)`
    .text-area {
        min-height: 16px;
    }
`

interface TeamTypeSelectFieldProps {
    isReadOnly?: boolean
    currentType?: string
    showError: boolean
}

export const TeamTypeSelectField = ({ isReadOnly, showError, currentType }: TeamTypeSelectFieldProps) => {
    const [t] = useTranslation()
    const [field, meta] = useField('type')
    const { setFieldValue } = useFormikContext()
    const teamConstants = useSelector(selectTeamConstants)
    const teamOptions = getTeamTypeOptions(t)
    const isInternalUser = useSelector(selectIsInternalUser)

    const teamTypeOptions = (() => {
        // external user will only see their current active team type
        if (currentType && !isInternalUser) {
            return teamConstants?.types.filter(type => type.id === currentType)
        }

        return teamConstants?.types
    })()

    return (
        <>
            <Label
                title={t('modalWindow.enterTeamTypeHeader')}
                subtext={t('modalWindow.enterTeamTypeSubText')}
                isRequired
                size="m"
            />

            <TeamTypeOptions>
                {teamTypeOptions?.map(value => (
                    <TeamTypeCard
                        key={value.id}
                        isSelected={value.id === field.value}
                        onClick={() => setFieldValue('type', value.id)}
                        isReadOnly={isReadOnly}
                    >
                        <Text scale="bodyM700">{teamOptions[value.id]?.title}</Text>
                        <Text scale="bodyS400">{teamOptions[value.id]?.subtext}</Text>
                    </TeamTypeCard>
                ))}
            </TeamTypeOptions>
            {showError && !!meta.error && <Text color="error">{meta.error}</Text>}
        </>
    )
}

const getBorderColor = ({
    theme,
    isDisabled,
    isSelected,
    isReadOnly,
    defaultValue,
}: {
    theme: Theme
    isDisabled?: boolean
    isSelected?: boolean
    isReadOnly?: boolean
    defaultValue: string
}) => {
    if (isDisabled) {
        return theme.colors.border.disable
    }

    if (isReadOnly) {
        return theme.colors.border.actionActive
    }

    if (isSelected) {
        return theme.colors.border.action
    }

    return defaultValue
}

const TeamTypeOptions = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${({ theme }) => theme.spacing[300]};
    flex-wrap: wrap;
`

const TeamTypeCard = styled.div<{ isSelected?: boolean; isDisabled?: boolean; isReadOnly?: boolean }>`
    box-sizing: border-box;
    min-height: 100px;
    max-width: 270px;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[200]};
    padding: 12px;
    border: 2px solid;
    background: ${({ theme }) => theme.colors.surface.primary};
    border-radius: 4px;
    cursor: pointer;

    border-color: ${({ theme, isSelected, isDisabled, isReadOnly }) =>
        getBorderColor({ theme, isSelected, isDisabled, isReadOnly, defaultValue: theme.colors.border.primary })};

    & > div {
        cursor: pointer;
    }
`
